import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function ScanInterface() {
  const navigate = useNavigate();
  const [target, setTarget] = useState('192.168.1.0/24');
  const [portRange, setPortRange] = useState('1-1024,8080');
  const [scanType, setScanType] = useState('Comprehensive');
  const [scanOutput, setScanOutput] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);

  const scanSimulations = [
    "[INFO] Initializing network adapter...",
    "[SCAN] Resolving target: 192.168.1.0/24...",
    "[SCAN] TCP SYN scan for ports 1-1024, 8080...",
    "[STATUS] Discovering active hosts...",
    "  Host 192.168.1.1 is UP (Router)",
    "  Host 192.168.1.10 is UP (Web Server)",
    "  Host 192.168.1.15 is UP (Workstation)",
    "  Host 192.168.1.20 is UP (IoT Device)",
    "[SCAN] Scanning 192.168.1.1 (Router):",
    "    Port 22/tcp open  (SSH)",
    "    Port 80/tcp open  (HTTP)",
    "    Port 443/tcp open (HTTPS)",
    "[SCAN] Scanning 192.168.1.10 (Web Server):",
    "    Port 21/tcp open  (FTP)",
    "    Port 80/tcp open  (HTTP)",
    "    Port 443/tcp open (HTTPS)",
    "    Port 8080/tcp open (Apache Tomcat)",
    "[VULN] Detected potential Shellshock vulnerability on 192.168.1.10 (HTTP/80).",
    "[SCAN] Scanning 192.168.1.15 (Workstation):",
    "    Port 139/tcp open (NetBIOS-SSN)",
    "    Port 445/tcp open (Microsoft-DS)",
    "[VULN] Possible SMBGhost detected on 192.168.1.15 (SMB/445).",
    "[SCAN] Scanning 192.168.1.20 (IoT Device):",
    "    Port 23/tcp open  (Telnet) - UNSAFE!",
    "    Port 80/tcp open  (HTTP)",
    "[ALERT] Weak credentials or default login suspected on 192.168.1.20 (Telnet/23).",
    "[INFO] OS Detection on 192.168.1.10: Linux Kernel 5.x",
    "[INFO] Scan Summary:",
    "  4 hosts up (100% done)",
    "  Vulnerabilities found: 3 Critical, 1 High",
    "[SUCCESS] Scan completed in 12.5 seconds.",
    "glocybs@console:~$ "
  ];

  const startScan = () => {
    setIsScanning(true);
    setScanOutput([]);
    setScanProgress(0);
    let currentLine = 0;

    const scanInterval = setInterval(() => {
      if (currentLine < scanSimulations.length) {
        setScanOutput(prev => [...prev, scanSimulations[currentLine]]);
        setScanProgress(Math.floor((currentLine / scanSimulations.length) * 100));
        currentLine++;
      } else {
        clearInterval(scanInterval);
        setIsScanning(false);
        setScanProgress(100);
      }
    }, 300); // Simulate typing speed

    return () => clearInterval(scanInterval);
  };

  const commonStyles = { // Re-using common styles from GlocybsHackingInterface
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0a1a 0%, #1a0a2a 50%, #0a0a1a 100%)',
      fontFamily: "'Inter', sans-serif",
      color: '#00e6e6',
      overflowX: 'hidden',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px',
      boxSizing: 'border-box',
    },
    '@keyframes subtleGridMove': {
      '0%': { backgroundPosition: '0 0' },
      '100%': { backgroundPosition: '100% 100%' },
    },
    '::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: `
        linear-gradient(to right, rgba(0, 230, 230, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(0, 230, 230, 0.05) 1px, transparent 1px)
      `,
      backgroundSize: '20px 20px',
      animation: 'subtleGridMove 60s linear infinite',
      opacity: 0.2,
      zIndex: 0,
    },
    headerBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: 'rgba(10, 10, 26, 0.95)',
      padding: '15px 30px',
      borderRadius: '8px',
      marginBottom: '15px',
      border: '1px solid rgba(0, 230, 230, 0.3)',
      boxShadow: '0 0 20px rgba(0, 230, 230, 0.2)',
      zIndex: 5,
      flexWrap: 'wrap',
      gap: '10px',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '10px 15px',
      },
    },
    headerSection: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      '@media (max-width: 768px)': {
        width: '100%',
        justifyContent: 'space-between',
        marginBottom: '5px',
      },
    },
    headerLabel: {
      fontSize: '11px',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      '@media (max-width: 480px)': {
        fontSize: '10px',
      },
    },
    headerValue: {
      fontSize: '14px',
      fontWeight: 'bold',
      color: '#00e6e6',
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    progressBar: {
      width: '60px',
      height: '4px',
      background: 'rgba(0, 230, 230, 0.2)',
      borderRadius: '2px',
      overflow: 'hidden',
      display: 'block',
      border: '1px solid rgba(0, 230, 230, 0.4)',
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(90deg, #00e6e6, #00aacc)',
      transition: 'width 0.1s ease',
      boxShadow: '0 0 6px #00e6e6',
    },
    mainContent: {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr', // Two columns for this page
      gap: '20px',
      flex: 1,
      zIndex: 2,
      '@media (max-width: 1024px)': { // Adjust breakpoint for this page
        gridTemplateColumns: '1fr', // Single column for smaller screens
      },
    },
    panel: {
      background: 'rgba(10, 10, 26, 0.8)',
      border: '1px solid rgba(0, 230, 230, 0.2)',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 0 15px rgba(0, 230, 230, 0.1)',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: '200px',
      '@media (max-width: 480px)': {
        padding: '15px',
      },
    },
    panelHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      fontSize: '15px',
      fontWeight: 'bold',
      color: '#00ffff',
      marginBottom: '15px',
      letterSpacing: '0.8px',
      borderBottom: '1px dashed rgba(0, 230, 230, 0.2)',
      paddingBottom: '10px',
      '@media (max-width: 480px)': {
        fontSize: '13px',
        marginBottom: '10px',
      },
    },
    panelIcon: {
      fontSize: '20px',
      lineHeight: 1,
      '@media (max-width: 480px)': {
        fontSize: '16px',
      },
    },
    formGroup: {
      marginBottom: '15px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '12px',
      color: '#888',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    input: {
      width: 'calc(100% - 20px)',
      padding: '10px',
      background: 'rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(0, 230, 230, 0.4)',
      borderRadius: '5px',
      color: '#00ffff',
      fontSize: '14px',
      fontFamily: "'Courier New', monospace",
      '&:focus': {
        outline: 'none',
        borderColor: '#00ffff',
        boxShadow: '0 0 8px rgba(0, 255, 255, 0.5)',
      },
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    select: {
      width: '100%',
      padding: '10px',
      background: 'rgba(0, 0, 0, 0.4)',
      border: '1px solid rgba(0, 230, 230, 0.4)',
      borderRadius: '5px',
      color: '#00ffff',
      fontSize: '14px',
      fontFamily: "'Courier New', monospace",
      appearance: 'none', // Remove default arrow
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='%2300FFFF' d='M9.293 12.95l-3.536-3.536.707-.707L10 11.536l3.536-3.536.707.707z'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 10px center',
      backgroundSize: '12px',
      '&:focus': {
        outline: 'none',
        borderColor: '#00ffff',
        boxShadow: '0 0 8px rgba(0, 255, 255, 0.5)',
      },
      '@media (max-width: 480px)': {
        fontSize: '12px',
      },
    },
    actionButton: {
      background: 'linear-gradient(45deg, #00ffff, #00aacc)',
      color: '#0a0a1a',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 25px',
      fontSize: '15px',
      fontWeight: 'bold',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.3s ease',
      boxShadow: '0 0 10px rgba(0, 255, 255, 0.4)',
      justifyContent: 'center',
      width: 'fit-content',
      margin: '20px auto 0',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 5px 20px rgba(0, 255, 255, 0.6)',
      },
      '&:disabled': {
        background: 'linear-gradient(45deg, #555, #333)',
        cursor: 'not-allowed',
        boxShadow: 'none',
        transform: 'none',
        color: '#888',
      },
      '@media (max-width: 480px)': {
        width: '100%',
        padding: '10px 15px',
        fontSize: '13px',
      },
    },
    terminalOutput: {
      background: 'rgba(0, 0, 0, 0.6)',
      border: '1px solid rgba(0, 255, 255, 0.3)',
      borderRadius: '5px',
      padding: '15px',
      flex: 1, // Allow it to fill available space
      overflowY: 'scroll',
      scrollbarWidth: 'thin',
      scrollbarColor: '#00ffff #0a0a1a',
      lineHeight: '1.5',
      fontSize: '11px',
      whiteSpace: 'pre-wrap',
      fontFamily: "'Courier New', monospace",
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-track': { background: '#0a0a1a', borderRadius: '3px' },
      '&::-webkit-scrollbar-thumb': { background: '#00ffff', borderRadius: '3px' },
      '@media (max-width: 480px)': {
        fontSize: '9px',
        padding: '10px',
      },
    },
    terminalLine: {
      marginBottom: '2px',
      color: '#00e6e6',
      textShadow: '0 0 2px rgba(0, 230, 230, 0.5)',
    },
    // Back button
    backButton: {
      background: 'linear-gradient(45deg, #444, #222)',
      color: '#00ffff',
      border: '1px solid #00ffff',
      borderRadius: '5px',
      padding: '8px 15px',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'absolute',
      top: '15px',
      left: '15px',
      zIndex: 10,
      '&:hover': {
        background: 'linear-gradient(45deg, #00ffff, #00aacc)',
        color: '#0a0a1a',
        boxShadow: '0 0 10px rgba(0, 255, 255, 0.6)',
      },
      '@media (max-width: 480px)': {
        padding: '6px 10px',
        fontSize: '10px',
        top: '10px',
        left: '10px',
      },
    },
  };

  return (
    <div style={commonStyles.container}>
      <button style={commonStyles.backButton} onClick={() => navigate('/')}>
        ← Back to Main
      </button>

      <div style={commonStyles.headerBar}>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>MODULE:</span>
          <span style={commonStyles.headerValue}>NETWORK SCANNER</span>
        </div>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>STATUS:</span>
          <span style={commonStyles.headerValue}>
            {isScanning ? 'SCANNING...' : 'READY'}
          </span>
        </div>
        <div style={commonStyles.headerSection}>
          <span style={commonStyles.headerLabel}>PROGRESS:</span>
          <span style={commonStyles.headerValue}>
            <span style={commonStyles.progressBar}>
              <div style={{...commonStyles.progressFill, width: `${scanProgress}%`}}></div>
            </span>
            {scanProgress}%
          </span>
        </div>
      </div>

      <div style={commonStyles.mainContent}>
        {/* Left Panel: Scan Configuration */}
        <div style={commonStyles.panel}>
          <div style={commonStyles.panelHeader}>
            <span style={commonStyles.panelIcon}>⚙️</span> SCAN CONFIGURATION
          </div>
          <div style={commonStyles.formGroup}>
            <label style={commonStyles.label} htmlFor="targetIp">Target IP / Range:</label>
            <input
              style={commonStyles.input}
              type="text"
              id="targetIp"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="e.g., 192.168.1.1 or 10.0.0.0/24"
              disabled={isScanning}
            />
          </div>
          <div style={commonStyles.formGroup}>
            <label style={commonStyles.label} htmlFor="portRange">Port Range:</label>
            <input
              style={commonStyles.input}
              type="text"
              id="portRange"
              value={portRange}
              onChange={(e) => setPortRange(e.target.value)}
              placeholder="e.g., 1-1024,8080,443"
              disabled={isScanning}
            />
          </div>
          <div style={commonStyles.formGroup}>
            <label style={commonStyles.label} htmlFor="scanType">Scan Type:</label>
            <select
              style={commonStyles.select}
              id="scanType"
              value={scanType}
              onChange={(e) => setScanType(e.target.value)}
              disabled={isScanning}
            >
              <option value="Comprehensive">Comprehensive Scan (SYN, OS, Vuln)</option>
              <option value="Quick">Quick Scan (SYN only)</option>
              <option value="Vulnerability">Vulnerability Scan</option>
              <option value="Stealth">Stealth Scan (FIN, ACK, Xmas)</option>
            </select>
          </div>
          <button
            style={commonStyles.actionButton}
            onClick={startScan}
            disabled={isScanning}
          >
            <span style={commonStyles.panelIcon}>▶️</span> {isScanning ? 'SCANNING...' : 'START SCAN'}
          </button>
        </div>

        {/* Right Panel: Live Scan Output */}
        <div style={commonStyles.panel}>
          <div style={commonStyles.panelHeader}>
            <span style={commonStyles.panelIcon}>🖥️</span> LIVE SCAN OUTPUT
          </div>
          <div style={commonStyles.terminalOutput}>
            {scanOutput.map((line, index) => (
              <div key={index} style={commonStyles.terminalLine}>
                {line}
              </div>
            ))}
            {isScanning && <div style={commonStyles.terminalLine}>_</div>} {/* Blinking cursor */}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ScanInterface;